<template>
  <div class="manage-players">
    <h2 class="title is-3">Manage players</h2>
    <p class="subtitle is-5">Remove players and edit scores</p>
    <b-input v-model="playerSearch" type="text" placeholder="Search for a player" />
    <div class="player-list pretty-scroll">
      <div v-for="player in filteredPlayers" :key="player.id" :class="{'editing-score': player.id === editingPlayerId}" class="player">
        <div class="flex-row">
          <div class="player-tag">
            <div v-if="showBusinessAvatars && player.avatar" class="avatar-svg-wrapper is-inline-icon">
              <span :style="{'mask-image': `url(${player.avatar})`}" class="avatar-svg-icon" />
            </div>
            <img v-else-if="showAvatar && player.avatar" :src="player.avatar">
            <p class="player-name">{{ player.name }}</p>
          </div>
          <div v-if="!isEditingScore(player)">
            <div @click="startRemovePlayer(player)" class="button is-light-bg">Remove</div>
            <div @click="startEditScore(player)" class="button is-light-bg">Edit Score</div>
          </div>
        </div>
        <div v-if="isEditingScore(player)" class="edit-score">
          <b-field label="Score" style="flex-grow: 2;">
            <b-input v-model="newPlayerScore" type="number" placeholder="Enter new score" />
          </b-field>

          <!-- <b-field label="Tiebreak count">
            <b-input v-model="newTiebreakScore" type="number" placeholder="Enter new tiebreak score" />
          </b-field> -->
          <div @click="editingPlayerId = null" class="button is-light-bg m-r-10">Cancel</div>
          <div @click="saveScore(player)" class="button is-light-bg">Save</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import addUserResponse from '~/queries/addUserResponse'
import { getAllPlayerScores, removePlayerById } from '~/queries/playerQueries'
import AvatarControls from '~/components/mixins/AvatarControls'
import { avatarStyles } from '~/common/avatars'

export default {
  mixins: [ AvatarControls ],
  props: [ 'players', 'eventId' ],
  data () {
    return {
      playerSearch: '',
      newPlayerScore: 0,
      newTiebreakScore: 0,
      editingPlayerId: null,
      scoresByPlayer: [],
      scoresSet: false,
      savingResponse: false
    }
  },
  computed: {
    filteredPlayers () {
      return this.players.filter((player) => {
        return player.name.toLowerCase().includes(this.playerSearch.toLowerCase())
      })
    },
    showBusinessAvatars () {
      return this.avatarStyle === avatarStyles.BUSINESS
    }
  },
  methods: {
    saveScore (player) {
      const score = this.getPlayerScore(player) || 0
      const scoreDifference = this.newPlayerScore - score

      // const tiebreakScore = this.getTiebreakScore(player) || 0
      // const tiebreakDifference = this.newTiebreakScore - tiebreakScore
      // const tiebreakText = tiebreakDifference ? `(+${tiebreakScore} tiebreak)` : ''
      // const tiebreakTextTwo = tiebreakDifference ? `(+${this.newTiebreakScore} tiebreak)` : ''

      const message = `<p style="display: block; margin-top: -11px;">Do you want to change <b>${player.name}</b>'s score?</p>
                       <p><br><b>Old score:</b> ${score} points</p>
                       <p><b>New score:</b> ${this.newPlayerScore} points</p>`

      this.$buefy.dialog.confirm({
        title: 'Confirm score adjustment',
        message,
        confirmText: 'Change score',
        cancelText: 'Cancel',
        type: 'is-info',
        onConfirm: () => {
          this.startSaveManualScoreAdjustment(player, scoreDifference, 0)
        }
      })
    },
    async startSaveManualScoreAdjustment (player, scoreDifference, tiebreakDifference) {
      this.log('saving manual score adjustment with score difference: ', scoreDifference, ' and tiebreak difference: ', tiebreakDifference)
      if (scoreDifference && scoreDifference !== 0) {
        this.log('adjusting main score by: ', scoreDifference)
        await this.saveManualScoreAdjustment(player, scoreDifference)
      }

      // if (tiebreakDifference && tiebreakDifference !== 0) {
      //   this.log('adjusting tiebreak score by: ', tiebreakDifference)
      //   await this.saveManualScoreAdjustment(player, 0, true)
      // }

      this.$buefy.toast.open({
        message: 'Score adjustment saved',
        type: 'is-success'
      })
    },
    async saveManualScoreAdjustment (player, scoreDifference, isTiebreak = false) {
      this.savingResponse = true
      const uniquePosition = Date.now() % 100000000 // last 8 digits from current timestamp

      const variables = {
        event: this.eventId,
        participant: player.id,
        prompt: { text: 'Manual score adjustment' },
        response: { text: 'Manual score adjustment' },
        isCorrect: true,
        shouldScore: true,
        isTiebreak,
        answerScore: scoreDifference || 0,
        answerScoreMultiplier: 1,
        voteScoreMultiplier: 1,
        userResponseTime: 5000,
        slidePosition: uniquePosition
      }

      await this.$apollo.mutate({
        mutation: addUserResponse,
        variables
      })

      this.savingResponse = false
      this.$emit('scoreUpdated')
    },
    async startEditScore (player) {
      this.editingPlayerId = player.id
      await this.setScoresByPlayer()
      this.newPlayerScore = this.getPlayerScore(player) || 0
      // this.newTiebreakScore = this.getTiebreakScore(player) || 0
    },
    cancelEditScore () {
      this.editingPlayerId = null
    },
    isEditingScore (player) {
      return player.id === this.editingPlayerId
    },
    getPlayerScore (player) {
      return this.scoresByPlayer.find(score => score.participantId === player.id)?.score
    },
    getTiebreakScore (player) {
      return this.scoresByPlayer.find(score => score.participantId === player.id)?.tiebreakCount
    },
    async setScoresByPlayer () {
      if (this.scoresSet) { return }
      const { data } = await this.$apollo.query({
        query: getAllPlayerScores,
        variables: {
          eventId: this.eventId
        },
        fetchPolicy: 'no-cache'
      })

      this.log('fetching participant scores: ', data)
      const participantScores = data.participantScores || []

      if (participantScores?.length > 0) {
        // remove any scores where the player id is not found in participants array
        const scores = participantScores.filter((score) => {
          return this.players.find(player => player.id === score.participantId) !== undefined
        })
        this.scoresByPlayer = Object.freeze(scores)
        this.scoresSet = true
      }
    },
    startRemovePlayer (player) {
      const playerAvatar = player.avatar ? `<img src="${player.avatar}" width="17px" height="17px"> ` : ''
      this.$buefy.dialog.confirm({
        title: 'Are you sure?',
        message: `Do you want to remove player <b>${playerAvatar}${player.name}</b>? This action is not reversible.`,
        confirmText: `Remove "${player.name}"`,
        cancelText: 'Cancel',
        type: 'is-danger',
        onConfirm: () => {
          this.removePlayer(player)
        }
      })
    },
    async removePlayer (player) {
      await this.$apollo.mutate({
        mutation: removePlayerById,
        variables: {
          id: player.id
        }
      })

      this.$emit('playerRemoved', player.id)
      this.$emit('close')
    }
  }
}
</script>
<style lang="scss" scoped>
.avatar-svg-wrapper {
  &.is-inline-icon {
    width: 1.4em;
    height: 1.4em;
  }
  .avatar-svg-icon {
    background: white;
  }
  margin-right: 10px;
}

.pretty-scroll {
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    width: 5px;
    margin:8px 10px 8px 0px;
  }

  &::-webkit-scrollbar-thumb {
    width: 5px;
    border-radius:5px;
    background-color: #ccc;
  }
}

.manage-players {
  background-color: white;
  padding: 30px;
  border-radius: 12px;
  max-width: 550px;
  margin: 0 auto;
}
.player-list {
  display: flex;
  flex-direction: column;
  height: 55vh;
  overflow-y: scroll;
  overflow-x: hidden;
  padding-right: .5em;
  border: 1px solid #eee;
  border-radius: 10px;
  padding: .5em .3em .5em .5em;
}
.edit-score {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 1dvi .4em .3em 0;

  .control {
    flex-grow: 1;
    margin-right: 1em;
  }

  ::v-deep .label {
    font-weight: normal;
    font-size: .7em;
    margin-bottom: .3em;
    opacity: .8;
  }

  .button {
    margin-top: .6em;
  }
}
.player {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  border-radius: 4px;
  padding: .3em .3em .3em .75em;
  margin-bottom: 2px;

  &:hover, &.editing-score {
    background-color: #f7f7f7;

    .button.is-light-bg {
      background-color: #d9eaf3;
    }
  }

  .flex-row {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .player-tag {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-size: 1.2em;
  }

  img {
    width: 1.2em;
    height: 1.2em;
    display: inline-block;
    margin-right: .5em;
  }
}
</style>
